import { computed, ref, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import useMediaStore from '../stores/media';
import { VideoPosition, VideoSize } from '../types';

type VideoResize = {
  x: number;
  y: number;
  w: number;
  h: number;
};

const useVideoOptions = () => {
  const mediaStore = useMediaStore();
  const { videoSize, videoPosition } = storeToRefs(mediaStore);
  const { setVideoSize, setVideoPosition } = mediaStore;

  const customSize = ref(false);
  const customPosition = ref(false);
  const fullScreen = ref(false);
  const draggable = ref(true);
  const resizable = ref(true);
  const localSize = ref<VideoSize>({ w: 640, h: 360 });
  const localPosition = ref<VideoPosition>({ x: 0, y: 0 });

  watchEffect(() => {
    if (fullScreen.value) return;
    localSize.value = videoSize.value;
    localPosition.value = videoPosition.value;
  });

  const isDraggable = computed(() => draggable.value);
  const isResizable = computed(() => resizable.value);

  const setVideoProps = (track: any) => {
    if (customPosition.value) {
      // We don't change the video position when changing page if the user set a custom position
      return;
    }

    let videoX = 0;
    let videoY = 0;
    const currentPage = parseInt(track.text.srcFile.split('/').slice(-1).pop(), 10);
    const pageOdd = currentPage !== 0 ? !!(currentPage % 2) : true;
    const { w: width } = videoSize.value;
    const customVideoX = pageOdd
      ? document.body.clientWidth / 4 - width / 2
      : (document.body.clientWidth / 4) * 3 - width / 2;

    if (!fullScreen.value) {
      videoX = customVideoX;
      videoY = 80;
    }

    setVideoPosition({ x: videoX, y: videoY });
  };

  const onVideoResize = (value: VideoResize) => {
    const { x, y, w, h } = value;
    setVideoPosition({ x, y });
    setVideoSize({ w, h });
    customPosition.value = true;
    customSize.value = true;
  };

  const onVideoDrag = (value: VideoPosition) => {
    setVideoPosition(value);
    customPosition.value = true;
  };

  const hasFitToVideo = ref(false);
  const fitToAspect = (aspect: number) => {
    if (hasFitToVideo.value || customSize.value || !aspect || !isFinite(aspect) || fullScreen.value) return;
    let targetW = videoSize.value.w;
    let targetH = Math.round(targetW / aspect);
    const maxH = typeof window !== 'undefined' ? Math.floor(window.innerHeight * 0.7) : targetH;
    if (targetH > maxH) {
      targetH = maxH;
      targetW = Math.round(targetH * aspect);
    }
    setVideoSize({ w: targetW, h: targetH });
    hasFitToVideo.value = true;
  };

  const toggleDraggableResizable = (toggle: boolean) => {
    draggable.value = toggle;
    resizable.value = toggle;
  };

  return {
    fullScreen,
    isDraggable,
    isResizable,
    localSize,
    localPosition,
    toggleDraggableResizable,
    setVideoProps,
    onVideoResize,
    onVideoDrag,
    fitToAspect,
  };
};

export default useVideoOptions;
